import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { BellIcon } from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import { useNotificationStore } from '../../stores/notificationStore';
import EmptyState, { PRESET_ICONS } from '../../components/common/EmptyState';
import { cn } from '../../lib/utils';

type DropdownPosition = {
  left: number;
  top: number;
};

const NotificationBell: React.FC = () => {
  const { token } = useAuthStore();
  const { notifications = [], unreadCount, fetchUnreadCount, fetchNotifications, markAsRead, markAllAsRead } = useNotificationStore();
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<DropdownPosition | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (token) {
      fetchUnreadCount(token);  
    }
  }, [token, fetchUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useLayoutEffect(() => {
    if (!isOpen) {
      setDropdownPosition(null);
      return;
    }

    const updateDropdownPosition = () => {
      const button = buttonRef.current;
      const panel = panelRef.current;
      if (!button || !panel) return;

      const viewportMargin = 12;
      const triggerRect = button.getBoundingClientRect();
      const panelRect = panel.getBoundingClientRect();
      const availableWidth = Math.max(0, window.innerWidth - viewportMargin * 2);
      const availableHeight = Math.max(0, window.innerHeight - viewportMargin * 2);
      const panelWidth = Math.min(panelRect.width, availableWidth);
      const panelHeight = Math.min(panelRect.height, availableHeight);
      const maxLeft = Math.max(viewportMargin, window.innerWidth - panelWidth - viewportMargin);
      const maxTop = Math.max(viewportMargin, window.innerHeight - panelHeight - viewportMargin);

      setDropdownPosition({
        left: Math.min(Math.max(triggerRect.left, viewportMargin), maxLeft),
        top: Math.min(Math.max(triggerRect.top - panelHeight - 8, viewportMargin), maxTop),
      });
    };

    updateDropdownPosition();
    window.addEventListener('resize', updateDropdownPosition);
    return () => window.removeEventListener('resize', updateDropdownPosition);
  }, [isOpen, notifications.length]);

  const handleToggle = async () => {
    const shouldOpen = !isOpen;
    if (shouldOpen && token) {
      await fetchNotifications(token, 1, 10);
    }
    setIsOpen(shouldOpen);
  };

  const handleNotificationClick = async (id: number) => {
    if (token) {
      await markAsRead(token, id);
    }
    setIsOpen(false);
    navigate(`/notifications/${id}`);
  };

  const handleMarkAllRead = async () => {
    if (token) {
      await markAllAsRead(token);
    }
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/notifications');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return '刚刚';
    if (minutes < 60) return `${minutes}分钟前`;
    if (hours < 24) return `${hours}小时前`;
    if (days < 7) return `${days}天前`;
    return date.toLocaleDateString('zh-CN');
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'SYSTEM':
        return (
          <div className="w-8 h-8 rounded-full bg-teal-500/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'PAYMENT':
        return (
          <div className="w-8 h-8 rounded-full bg-green-500/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
        );
      case 'AI':
        return (
          <div className="w-8 h-8 rounded-full bg-emerald-500/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-8 h-8 rounded-full bg-stone-500/10 flex items-center justify-center">
            <svg className="w-4 h-4 text-stone-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </div>
        );
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className={cn(
          'relative flex h-9 w-9 items-center justify-center rounded-lg text-content-muted transition-colors hover:bg-surface-hover hover:text-content-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
          isOpen && 'bg-surface-hover text-content-primary'
        )}
        aria-label="通知"
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        title="通知"
      >
        <BellIcon className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute -right-1 -top-1 flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-semantic-danger px-1 text-[10px] font-bold leading-none text-white">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div
          ref={panelRef}
          role="dialog"
          aria-label="通知列表"
          className={cn(
            'fixed z-[200] animate-fade-in-up',
            !dropdownPosition && 'pointer-events-none opacity-0'
          )}
          style={{
            left: dropdownPosition?.left ?? 0,
            top: dropdownPosition?.top ?? 0,
            width: 'min(20rem, calc(100vw - 1.5rem))',
          }}
        >
          <div
            className="card flex flex-col overflow-hidden shadow-xl shadow-black/20"
            style={{ maxHeight: 'min(28rem, calc(100vh - 1.5rem))' }}
          >
            <div className="flex items-center justify-between px-4 py-3" style={{ borderBottom: '1px solid var(--border-color)' }}>
              <h3 className="font-semibold" style={{ color: 'var(--text-primary)' }}>通知</h3>
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-emerald-500 hover:text-emerald-600 transition-colors"
                >
                  全部已读
                </button>
              )}
            </div>

            <div className="min-h-0 flex-1 overflow-y-auto">
              {notifications.length === 0 ? (
                <EmptyState
                  icon={PRESET_ICONS.notification}
                  title="暂无通知"
                  size="sm"
                />
              ) : (
                notifications.slice(0, 5).map((notification) => (
                  <button
                    key={notification.id}
                    type="button"
                    onClick={() => handleNotificationClick(notification.id)}
                    aria-label={`查看通知 ${notification.title}`}
                    className="flex w-full items-start gap-3 border-0 px-4 py-3 text-left transition-colors hover:bg-emerald-500/5"
                    style={{ borderBottom: '1px solid var(--border-color)' }}
                  >
                    {getTypeIcon(notification.type)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className={`text-sm font-medium truncate ${!notification.isRead ? 'font-semibold' : ''}`} style={{ color: 'var(--text-primary)' }}>
                          {notification.title}
                        </p>
                        {!notification.isRead && (
                          <span className="w-2 h-2 bg-emerald-500 rounded-full flex-shrink-0"></span>
                        )}
                      </div>
                      <p className="text-xs mt-0.5 line-clamp-2" style={{ color: 'var(--text-muted)' }}>
                        {notification.content}
                      </p>
                      <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                        {formatDate(notification.createdAt)}
                      </p>
                    </div>
                  </button>
                ))
              )}
            </div>

            {notifications.length > 0 && (
              <div className="px-4 py-2 text-center" style={{ borderTop: '1px solid var(--border-color)' }}>
                <button
                  onClick={handleViewAll}
                  className="text-sm text-emerald-500 hover:text-emerald-600 transition-colors"
                >
                  查看全部
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
